import * as React from "react";
import { Alert, Select, Tag } from "antd";
import type { CustomTagProps } from "rc-select/lib/BaseSelect";
import type { TaskStatus } from "../types";
import type {
    StandardWorkflowTargetStatus,
    TaskStatusFlowModeStatusMapping,
} from "../../task_types/types";
import AppModal from "../../common/components/AppModal";

type MappingState = Record<StandardWorkflowTargetStatus, string[]>;

type Props = {
    open: boolean;
    isLoading: boolean;
    taskTypeName?: string;
    statuses: TaskStatus[];
    onCancel: () => void;
    onConfirm: (statusMappings: TaskStatusFlowModeStatusMapping[]) => void;
};

const standardStatuses: Array<{
    key: StandardWorkflowTargetStatus;
    label: string;
    color: string;
    borderColor: string;
    background: string;
}> = [
    {
        key: "open",
        label: "Open",
        color: "#2F5FE3",
        borderColor: "#2F5FE3",
        background: "#EEF4FF",
    },
    {
        key: "inProgress",
        label: "In Progress",
        color: "#D49B00",
        borderColor: "#D49B00",
        background: "#FFF9E6",
    },
    {
        key: "blocked",
        label: "Blocked",
        color: "#EB4D4B",
        borderColor: "#EB4D4B",
        background: "#FFF1F0",
    },
    {
        key: "done",
        label: "Done",
        color: "#31A24C",
        borderColor: "#31A24C",
        background: "#F0FFF4",
    },
];

const emptyMappings = (): MappingState => ({
    open: [],
    inProgress: [],
    blocked: [],
    done: [],
});

const RevertWorkflowModal: React.FC<Props> = ({
    open,
    isLoading,
    taskTypeName,
    statuses,
    onCancel,
    onConfirm,
}) => {
    const [mappings, setMappings] = React.useState<MappingState>(emptyMappings);

    React.useEffect(() => {
        if (!open) {
            return;
        }

        setMappings(emptyMappings());
    }, [open]);

    const statusesById = React.useMemo(() => {
        return statuses.reduce<Record<string, TaskStatus>>((acc, status) => {
            acc[status.id] = status;
            return acc;
        }, {});
    }, [statuses]);

    const allMappedStatusIds = React.useMemo(() => {
        return Object.values(mappings).flat();
    }, [mappings]);

    const hasDuplicateMappings =
        new Set(allMappedStatusIds).size !== allMappedStatusIds.length;
    const hasFullMapping =
        statuses.length > 0 &&
        allMappedStatusIds.length === statuses.length &&
        !hasDuplicateMappings;
    const canConfirm = hasFullMapping && !isLoading;

    const getOptionsForTarget = React.useCallback(
        (target: StandardWorkflowTargetStatus) => {
            const selectedByOthers = new Set(
                standardStatuses
                    .filter((status) => status.key !== target)
                    .flatMap((status) => mappings[status.key]),
            );

            return statuses
                .filter(
                    (status) =>
                        !selectedByOthers.has(status.id) ||
                        mappings[target].includes(status.id),
                )
                .map((status) => ({
                    label: status.name,
                    value: status.id,
                }));
        },
        [mappings, statuses],
    );

    const handleMappingChange = React.useCallback(
        (target: StandardWorkflowTargetStatus, selectedIds: string[]) => {
            setMappings((current) => ({
                ...current,
                [target]: selectedIds,
            }));
        },
        [],
    );

    const handleConfirm = React.useCallback(() => {
        if (!canConfirm) {
            return;
        }

        const statusMappings = standardStatuses.flatMap((standardStatus) =>
            mappings[standardStatus.key].map((sourceStatusId) => ({
                sourceStatusId,
                targetStandardStatus: standardStatus.key,
            })),
        );

        onConfirm(statusMappings);
    }, [canConfirm, mappings, onConfirm]);

    return (
        <AppModal
            centered
            width={1120}
            open={open}
            onCancel={onCancel}
            className="alpha-modal"
            cancelButtonTitle="Cancel"
            onCancelClick={onCancel}
            cancelButtonProps={{
                disabled: isLoading,
            }}
            submitButtonTitle="Switch back to standard workflow"
            onSubmitClick={handleConfirm}
            submitButtonProps={{
                loading: isLoading,
                disabled: !canConfirm,
            }}
        >
            <div
                style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#13254C",
                    marginBottom: 24,
                }}
            >
                Switch back to standard workflow
            </div>

            <Alert
                type="warning"
                showIcon
                style={{
                    marginBottom: 32,
                    borderRadius: 12,
                    border: "1px solid #F5C27A",
                    background: "#FFF7E8",
                }}
                message={
                    <div
                        style={{
                            color: "#13254C",
                            lineHeight: 1.7,
                            fontSize: 15,
                        }}
                    >
                        This will replace the current custom workflow for{" "}
                        <strong>{taskTypeName ?? "this task type"}</strong> with
                        the standard workflow: <strong>Open</strong>,{" "}
                        <strong>In Progress</strong>, <strong>Blocked</strong>,
                        and <strong>Done</strong>. Custom statuses, transitions,
                        and status action assignments may be removed or changed.
                        Existing tasks and subtasks using current statuses will
                        be moved to the mapped standard statuses after
                        switching.
                    </div>
                }
            />

            <div style={{ marginBottom: 16 }}>
                <div
                    style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#13254C",
                        marginBottom: 8,
                    }}
                >
                    Map existing custom statuses to the standard workflow
                </div>
                <div
                    style={{
                        fontSize: 14,
                        color: "#5B6B8C",
                        lineHeight: 1.7,
                    }}
                >
                    After switching back, this task type will use only the
                    standard statuses. Select where tasks from the current main
                    statuses should be moved.
                </div>
            </div>

            <div
                style={{
                    border: "1px solid #E6ECF5",
                    borderRadius: 12,
                    overflow: "hidden",
                    marginBottom: 24,
                }}
            >
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "320px 1fr",
                        background: "#F7F9FC",
                        borderBottom: "1px solid #E6ECF5",
                        fontWeight: 700,
                        color: "#42526E",
                    }}
                >
                    <div style={{ padding: "18px 20px" }}>
                        Standard status (target)
                    </div>
                    <div style={{ padding: "18px 20px" }}>
                        Existing custom statuses to move here
                    </div>
                </div>

                {standardStatuses.map((standardStatus, index) => (
                    <div
                        key={standardStatus.key}
                        style={{
                            display: "grid",
                            gridTemplateColumns: "320px 1fr",
                            borderBottom:
                                index === standardStatuses.length - 1
                                    ? "none"
                                    : "1px solid #E6ECF5",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 14,
                                padding: "18px 20px",
                            }}
                        >
                            <span
                                style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 999,
                                    border: `2px solid ${standardStatus.borderColor}`,
                                    background: standardStatus.background,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: standardStatus.color,
                                    fontWeight: 700,
                                }}
                            >
                                {standardStatus.key === "inProgress"
                                    ? "◻"
                                    : standardStatus.key === "blocked"
                                      ? "−"
                                      : standardStatus.key === "done"
                                        ? "✓"
                                        : ""}
                            </span>
                            <span
                                style={{
                                    fontSize: 15,
                                    color: "#13254C",
                                    fontWeight: 500,
                                }}
                            >
                                {standardStatus.label}
                            </span>
                        </div>

                        <div style={{ padding: "14px 20px" }}>
                            <Select
                                mode="multiple"
                                allowClear
                                style={{ width: "100%" }}
                                placeholder="Select statuses"
                                value={mappings[standardStatus.key]}
                                options={getOptionsForTarget(
                                    standardStatus.key,
                                )}
                                onChange={(values) =>
                                    handleMappingChange(
                                        standardStatus.key,
                                        values,
                                    )
                                }
                                optionFilterProp="label"
                                tagRender={(props: CustomTagProps) => {
                                    const status =
                                        statusesById[props.value as string];
                                    return (
                                        <Tag
                                            closable={props.closable}
                                            onClose={props.onClose}
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                padding: "4px 10px",
                                                marginInlineEnd: 8,
                                                marginBlock: 4,
                                                borderRadius: 8,
                                                background: "#F3F5F8",
                                                border: "1px solid #D9E0EA",
                                                color: "#42526E",
                                            }}
                                        >
                                            {status?.name ?? props.label}
                                        </Tag>
                                    );
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <Alert
                type={canConfirm ? "info" : "warning"}
                showIcon
                style={{
                    borderRadius: 12,
                    marginBottom: 8,
                }}
                message={
                    canConfirm
                        ? "All tasks in the selected statuses will be moved to the mapped standard statuses after switching."
                        : "Map every current main status exactly once before switching back to the standard workflow."
                }
            />
        </AppModal>
    );
};

export default RevertWorkflowModal;
